import { FC } from 'react'
import { Box, Link, Typography } from '@mui/material';
import { Component } from '../../types/ContentStructure'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

interface IComponentLinkProps {
    component: Component
    sx?: any
    // Cuando es true fuerza estilo CTA botón (usado desde ComponentBanner/Carrusel)
    variant?: 'rrss' | 'cta' | 'text'
}

// Detecta si el icon es una imagen de red social (cuadrada, PNG/SVG externo)
// Los CTAs de banner usan iconos tipo flecha que NO son cuadrados/PNG de RRSS
function detectVariant(component: Component): 'rrss' | 'cta' | 'text' {
    const { icon, title } = component
    const hasIcon = !!(icon && (icon.startsWith('http') || icon.startsWith('data:')))
    const hasTitle = !!(title && title.trim().length > 0)

    if (!hasIcon && !hasTitle) return 'text'
    if (hasIcon && !hasTitle) return 'rrss'   // ícono puro: RRSS

    // Tiene icon + title:
    // Si el icon es una imagen cuadrada (PNG típico de RRSS), mostramos ícono + texto como RRSS
    // Si el icon es una flecha/arrow pequeña, mostramos como CTA botón
    // Distinción: los iconos RRSS tienen URLs que contienen palabras clave de redes sociales
    // o son imágenes que los admins suben. Los CTAs de banner usan iconos de arrow inline.
    // La forma más robusta: si hasIcon y hasTitle, pero el icon parece ser RRSS (cuadrado grande)
    // → mostramos ícono + texto label (RRSS con nombre)
    // En la práctica en Avantthya:
    //   RRSS en footer: hasIcon + hasTitle → queremos mostrar el ÍCONO (no el texto)
    //   CTAs en banner: hasIcon + hasTitle → queremos mostrar TEXTO + flecha
    // Diferencia real: el componentName o el parent type. Sin ese contexto, usamos heurística:
    // Si el icon es una imagen grande (PNG de RRSS), priorizamos el ícono.
    // Si el icon es pequeño/arrow, priorizamos el texto.
    // Para el footer de Avantthya, los RRSS tienen icon que termina en .png y title con nombre.
    // → Mostramos ícono (sin texto) para mantener el diseño del footer.
    // → Para CTAs de banner, el parent llama con variant='cta' explícitamente.
    if (hasIcon) return 'rrss'  // si tiene ícono, prioritizamos el ícono
    return 'text'
}

const ComponentLink: FC<IComponentLinkProps> = ({ component, sx, variant }) => {
    const { icon, title, link, theme, subTitle } = component

    const resolvedVariant = variant ?? detectVariant(component)

    // ── RRSS: muestra el ícono (con o sin título debajo) ──────────────────────
    if (resolvedVariant === 'rrss') {
        return (
            <Link
                href={link ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                sx={[
                    theme.themeStyles,
                    {
                        p: 0,
                        borderRadius: '0.5rem',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        transition: 'opacity 0.2s, transform 0.2s',
                        width: 'fit-content',
                        '&:hover': { opacity: 0.75, transform: 'scale(1.08)' },
                    },
                    sx
                ]}
            >
                {icon && (
                    <Box
                        component="img"
                        src={icon}
                        alt={title ?? ''}
                        sx={{ width: { xs: 42, md: 46 }, height: { xs: 42, md: 46 }, objectFit: 'contain' }}
                    />
                )}
            </Link>
        )
    }

    // ── CTA: botón con texto + flecha ─────────────────────────────────────────
    if (resolvedVariant === 'cta') {
        return (
            <Link
                href={link ?? '#'}
                target={link?.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                sx={[{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1, md: 1.25 },
                    borderRadius: 1,
                    fontWeight: 700,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    backgroundColor: theme.themeStyles.backgroundColor || 'transparent',
                    color: theme.themeStyles.color || 'white',
                    border: `2px solid ${theme.themeStyles.color || 'white'}`,
                    '&:hover': { opacity: 0.85, transform: 'translateY(-1px)' },
                }, sx]}
            >
                <Typography variant="button" sx={[{
                    color: 'inherit',
                    fontWeight: 700,
                    lineHeight: 1,
                }, sx]}>
                    {title}
                </Typography>

                {icon && (
                    <Box
                        component="img"
                        src={icon}
                        alt={title ?? ''}
                        sx={{ width: { xs: 10, md: 14 }, height: { xs: 10, md: 14 }, objectFit: 'contain' }}
                    />
                )}
            </Link>
        )
    }

    // ── Texto simple: footer, nav, subtítulos ─────────────────────────────────
    return (
        <Link
            href={link ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            sx={[
                theme.themeStyles,
                {
                    textDecoration: 'none',
                    display: 'block',
                    width: '100%',
                    py: 0.25,
                    transition: 'opacity 0.2s',
                    '&:hover': { opacity: 0.75 },
                    ...sx,
                }
            ]}
        >
            <Typography sx={sx}>
                {title}
            </Typography>

            <Typography sx={sx}>
                {subTitle}
            </Typography>
        </Link>
    )
}

export default ComponentLink
